import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from 'react'
import useSWR from 'swr'
import {
  maintenanceTicketsActions,
  maintenanceTicketsInitialState,
  maintenanceTicketsReducer,
  MaintenanceTicketsState,
} from '@/reducer'
import {
  searchAllBranchManagerTickets,
  searchAllMaintenanceTicketsProcessed,
  searchAllMaintenanceTicketsProcessing,
  searchContractorQuotation,
  searchPurchaseOrder,
  searchPurchaseRequest,
  searchTicketAuditTrail,
} from '@/api/maintenance-tickets-api'
import {
  AuditTrailData,
  ContractorQuotationData,
  MaintenanceTabType,
  PurchaseOrderData,
  PurchaseRequestData,
  TicketsData,
} from '@/utilities/types'
import { useTicketState, useDebounce } from '@/utilities'
import { useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
// const local = `http://192.168.1.230:8082/api`

type MaintenanceTicketsContextType = {
  state: MaintenanceTicketsState
  actions: typeof maintenanceTicketsActions
  dispatch: React.Dispatch<any>
  processing: TicketsData[] | undefined
  processed: TicketsData[] | undefined
  processingState: ReturnType<typeof useTicketState>
  processedState: ReturnType<typeof useTicketState>
  searchTickets: (
    type: 'processing' | 'processed' | 'bm',
    keyword: string,
  ) => void
  filterTickets: (
    type: 'processing' | 'processed' | 'bm',
    filters: DateFilters,
  ) => void
  resetFilters: (type: 'processing' | 'processed' | 'bm') => void
  selectedTab: MaintenanceTabType
  setSelectedTab: (tab: MaintenanceTabType) => void
  role: string
  bmProcessed: TicketsData[] | undefined
  bmState: ReturnType<typeof useTicketState>
  auditTrail: AuditTrailData[] | undefined
  auditTrailState: ReturnType<typeof useAuditTrailState>
  viewTicketAuditTrail: (ticketId: number) => void
  filterAuditTrail: (filters: DateFilters) => void
  resetAuditTrailFilter: (type: string) => void
  mutateBmProcessed: () => Promise<any>
  mutateForProcessing: () => Promise<any>
  filterDataAuditTrail: (data: string) => void
  contractorQuotation: ContractorQuotationData[] | undefined
  contractorQuotationState: ReturnType<typeof useContractorQuotationState>
  viewQuotationForm: (ticketId: number) => void
  mutateContractorQuotation: () => Promise<any>
  viewPurchaseRequest: (ticketId: number) => void
  mutatePurchaseRequest: () => Promise<any>
  purchaseRequestState: ReturnType<typeof usePurchaseRequestTicket>
  purchaseRequest: PurchaseRequestData[] | undefined
  purchaseOrder: PurchaseOrderData[] | undefined
  purchaseOrderState: ReturnType<typeof usePurchaseOrderTicket>
  viewPurchaseOrder: (ticketId: number) => void
  mutatePurchaseOrder: () => Promise<any>
  filterTicketsStatus: (
    type: 'processing' | 'processed' | 'bm',
    status: string,
  ) => void
  filterTicketsMethodRepair: (
    type: 'processing' | 'processed' | 'bm',
    method: string,
  ) => void
  filterTicketsTypeConcern: (
    type: 'processing' | 'processed' | 'bm',
    typeConcern: string,
  ) => void
  mutateProcessed: () => Promise<any>
  filterSortedTickets: (
    type: 'processing' | 'processed' | 'bm',
    method: string,
  ) => void
}

const MaintenanceTicketsContext = createContext<
  MaintenanceTicketsContextType | undefined
>(undefined)

export const useMaintenanceTicketsContext =
  (): MaintenanceTicketsContextType => {
    const context = useContext(MaintenanceTicketsContext)
    if (!context) {
      throw new Error(
        'useMaintenanceTicketsContext must be used within a MaintenanceTicketsContextProvider',
      )
    }
    return context
  }

type DateFilters = {
  startCreatedDate?: string
  endCreatedDate?: string
  startModifiedDate?: string
  endModifiedDate?: string
}

const useAuditTrailState = () => {
  const [ticketId, setTicketId] = useState(0)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [dateFilters, setDateFilters] = useState<DateFilters>({
    startCreatedDate: '',
    endCreatedDate: '',
  })
  const [dataFilters, setDataFilters] = useState('')
  const [totalElements, setTotalElements] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  return {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    keyword,
    setKeyword,
    dateFilters,
    setDateFilters,
    totalElements,
    setTotalElements,
    isLoading,
    setIsLoading,
    ticketId,
    setTicketId,
    dataFilters,
    setDataFilters,
  }
}

const useContractorQuotationState = () => {
  const [ticketId, setTicketId] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  return {
    ticketId,
    setTicketId,
    isLoading,
    setIsLoading,
  }
}

const usePurchaseRequestTicket = () => {
  const [ticketId, setTicketId] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  return {
    ticketId,
    setTicketId,
    isLoading,
    setIsLoading,
  }
}

const usePurchaseOrderTicket = () => {
  const [ticketId, setTicketId] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  return {
    ticketId,
    setTicketId,
    isLoading,
    setIsLoading,
  }
}

export const MaintenanceTicketsContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const processingState = useTicketState()
  const processedState = useTicketState()
  const bmState = useTicketState()
  const auditTrailState = useAuditTrailState()
  const contractorQuotationState = useContractorQuotationState()
  const purchaseRequestState = usePurchaseRequestTicket()
  const purchaseOrderState = usePurchaseOrderTicket()
  const [processedFlag, setProcessedFlag] = useState<boolean | undefined>(
    undefined,
  )
  console.log('processed state ', processingState)

  const role = JSON.parse(localStorage.getItem('role') || '')
  const userId = JSON.parse(localStorage.getItem('userId') || '')
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const isTicketNo = queryParams.get('ticketNo')

  const isProcessedParam = queryParams.get('isProcessed')
  const [selectedTab, setSelectedTab] = useState<MaintenanceTabType>(() => {
    if (isProcessedParam === 'true') return 'Processed'
    return role === 'Branch Manager' || role === 'OIC'
      ? 'For BM'
      : 'For Processing'
  })

  const handleTabChange = useCallback((tab: MaintenanceTabType) => {
    setSelectedTab(tab)
    const state = tab === 'For Processing' ? processingState : processedState
    state.setPage(0)
    state.setKeyword('')
    state.setSort('dateModified,desc')
    state.setStatus('')
    state.setMethodOfRepair('')
    state.setTypeOfConcern('')
    state.setDateFilters({
      startCreatedDate: '',
      endCreatedDate: '',
      startModifiedDate: '',
      endModifiedDate: '',
    })

    if (role === 'Branch Manager') {
      setProcessedFlag(null)
    } else {
      setProcessedFlag(tab === 'Processed')
    }
  }, [])

  const fetchTickets = async (
    type: 'processing' | 'processed' | 'bm',
    isProcessed?: boolean,
  ) => {
    const state =
      type === 'bm'
        ? bmState
        : type === 'processing'
          ? processingState
          : processedState

    let apiCall

    if (isTicketNo != null) {
      state.setKeyword(isTicketNo)
    }

    if (isProcessedParam === 'true') {
      apiCall = searchAllMaintenanceTicketsProcessed
      isProcessed = true
    } else {
      apiCall =
        type === 'bm'
          ? searchAllBranchManagerTickets
          : type === 'processing'
            ? searchAllMaintenanceTicketsProcessing
            : searchAllMaintenanceTicketsProcessed
    }

    state.setIsLoading(true)

    try {
      const df = state.dateFilters
      const rawParams = {
        page: state.page,
        keyword: state.keyword,
        size: state.rowsPerPage,
        sort: state.sort.split(',')[0],
        order: state.sort.split(',')[1],
        sinceCreated: df.startCreatedDate,
        untilCreated: df.endCreatedDate,
        sinceModified: df.startModifiedDate,
        untilModified: df.endModifiedDate,
        status: state.status,
        methodOfRepair: state.methodOfRepair,
        typeOfConcern: state.typeOfConcern,
        roleName: role,
        userID: userId,
        isProcessed: isProcessed,
      }

      const cleanParams = Object.fromEntries(
        Object.entries(rawParams).filter(
          ([, v]) => v !== undefined && v !== null && v !== '',
        ),
      )

      const response = await apiCall(cleanParams)
      const content = response?.data?.data?.content ?? []
      const totalElements = response?.data?.data?.totalElements ?? 0

      // double check if the value is empty will return o
      if (!Array.isArray(content) || content.length === 0) {
        state.setTotalElements(0)
        return []
      }

      state.setTotalElements(totalElements)

      const newParams = new URLSearchParams(location.search)
      newParams.delete('ticketNo')
      newParams.delete('isProcessed')

      navigate(
        {
          pathname: location.pathname,
          search: newParams.toString(),
        },
        { replace: true },
      )

      return content
    } catch (error) {
      console.error(`Failed to fetch ${type} tickets:`, error)
    } finally {
      state.setIsLoading(false)
    }
  }

  const searchTickets = useDebounce(
    (type: 'processing' | 'processed' | 'bm', keyword: string) => {
      const state =
        type === 'bm'
          ? bmState
          : type === 'processing'
            ? processingState
            : processedState

      state.setKeyword(keyword)
      state.setPage(0)
    },
  )

  const viewTicketAuditTrail = useDebounce((ticketId: number) => {
    auditTrailState.setTicketId(ticketId)
    auditTrailState.setPage(1)
  })

  const viewQuotationForm = useDebounce((ticketId: number) => {
    contractorQuotationState.setTicketId(ticketId)
  })

  const viewPurchaseRequest = useDebounce((ticketId: number) => {
    purchaseRequestState.setTicketId(ticketId)
  })

  const viewPurchaseOrder = useDebounce((ticketId: number) => {
    purchaseOrderState.setTicketId(ticketId)
  })

  const filterTickets = useDebounce(
    (type: 'processing' | 'processed' | 'bm', filters: DateFilters) => {
      const state =
        type === 'bm'
          ? bmState
          : type === 'processing'
            ? processingState
            : processedState
      state.setDateFilters(filters)
      state.setPage(0)
    },
  )

  const filterTicketsStatus = useDebounce(
    (type: 'processing' | 'processed' | 'bm', status: string) => {
      const state =
        type === 'bm'
          ? bmState
          : type === 'processing'
            ? processingState
            : processedState
      state.setStatus(status)
      state.setPage(0)
    },
  )

  const filterTicketsMethodRepair = useDebounce(
    (type: 'processing' | 'processed' | 'bm', method: string) => {
      const state =
        type === 'bm'
          ? bmState
          : type === 'processing'
            ? processingState
            : processedState
      state.setMethodOfRepair(method)
      state.setPage(0)
    },
  )

  const filterSortedTickets = useDebounce(
    (type: 'processing' | 'processed' | 'bm', sort: string) => {
      const state =
        type === 'bm'
          ? bmState
          : type === 'processing'
            ? processingState
            : processedState
      state.setSort(sort)
      state.setPage(0)
    },
  )

  const filterTicketsTypeConcern = useDebounce(
    (type: 'processing' | 'processed' | 'bm', typeConcern: string) => {
      const state =
        type === 'bm'
          ? bmState
          : type === 'processing'
            ? processingState
            : processedState
      state.setTypeOfConcern(typeConcern)
      state.setPage(0)
    },
  )

  const filterAuditTrail = useDebounce((filters: DateFilters) => {
    auditTrailState.setDateFilters(filters)
    auditTrailState.setPage(1)
  })

  const filterDataAuditTrail = useDebounce((data: string) => {
    auditTrailState.setDataFilters(data)
    auditTrailState.setPage(1)
  })

  const resetFilters = (type: 'processing' | 'processed' | 'bm') => {
    const state =
      type === 'bm'
        ? bmState
        : type === 'processing'
          ? processingState
          : processedState
    state.setKeyword('')
    state.setStatus('')
    state.setSort('')
    state.setMethodOfRepair('')
    state.setTypeOfConcern('')
    state.setDateFilters({
      startCreatedDate: '',
      endCreatedDate: '',
      startModifiedDate: '',
      endModifiedDate: '',
    })
    state.setPage(0)
  }

  const resetAuditTrailFilter = (type: string) => {
    switch (type) {
      case 'RESETFILTER':
        auditTrailState.setDateFilters({
          startCreatedDate: '',
          endCreatedDate: '',
          startModifiedDate: '',
          endModifiedDate: '',
        })
        break
      default:
        auditTrailState.setDateFilters({
          startCreatedDate: '',
          endCreatedDate: '',
          startModifiedDate: '',
          endModifiedDate: '',
        })
        auditTrailState.setTicketId(0)
        auditTrailState.setDataFilters('')
        break
    }
  }

  const { data: processing, mutate: mutateForProcessing } = useSWR(
    selectedTab === 'For Processing'
      ? `/v2/ticket-view/search/?keyword=${processingState.keyword}&sort=${processingState.sort}&page=${processingState.page}&size=${processingState.rowsPerPage}&sinceCreated=${processingState.dateFilters.startCreatedDate}&untilCreated=${processingState.dateFilters.endCreatedDate}&sinceModified=${processingState.dateFilters.startModifiedDate}&untilModified=${processingState.dateFilters.endModifiedDate}&methodOfRepair=${processingState.methodOfRepair}&typeOfConcern=${processingState.typeOfConcern}&status=${processingState.status}&roleName=${role}&userID=${userId}&isProcessed=false`
      : null,
    () => fetchTickets('processing', false),
    { revalidateOnFocus: false },
  )

  const { data: processed, mutate: mutateProcessed } = useSWR(
    selectedTab === 'Processed'
      ? `/v2/ticket-view/search/?keyword=${processedState.keyword}&sort=${processedState.sort}&page=${processedState.page}&size=${processedState.rowsPerPage}&sinceCreated=${processedState.dateFilters.startCreatedDate}&untilCreated=${processedState.dateFilters.endCreatedDate}&sinceModified=${processedState.dateFilters.startModifiedDate}&untilModified=${processedState.dateFilters.endModifiedDate}&methodOfRepair=${processedState.methodOfRepair}&typeOfConcern=${processedState.typeOfConcern}&status=${processedState.status}&roleName=${role}&userID=${userId}&isProcessed=true`
      : null,
    () => fetchTickets('processed', true),
    { revalidateOnFocus: false },
  )

  const { data: bmProcessed, mutate: mutateBmProcessed } = useSWR(
    selectedTab === 'For BM'
      ? `/v2/ticket-view/search/?keyword=${bmState.keyword}&sort=${bmState.sort}&page=${bmState.page}&size=${bmState.rowsPerPage}&sinceCreated=${bmState.dateFilters.startCreatedDate}&untilCreated=${bmState.dateFilters.endCreatedDate}&sinceModified=${bmState.dateFilters.startModifiedDate}&untilModified=${bmState.dateFilters.endModifiedDate}&methodOfRepair=${bmState.methodOfRepair}&typeOfConcern=${bmState.typeOfConcern}&status=${bmState.status}&roleName=${role}&userId=${userId}`
      : null,
    () => fetchTickets('bm', true),
    { revalidateOnFocus: false },
  )

  const fetchTicketAuditTrails = async () => {
    auditTrailState.setIsLoading(true)
    try {
      const response = await searchTicketAuditTrail({
        ticketId: auditTrailState.ticketId,
        page: auditTrailState.page,
        keyword: auditTrailState.keyword,
        size: auditTrailState.rowsPerPage,
        sort: 'date_created,DESC',
        dataFilters: auditTrailState.dataFilters,
        ...auditTrailState.dateFilters,
        pageable: {},
      })
      auditTrailState.setTotalElements(response.data.totalElements)
      return response.data.data
    } catch (error) {
      console.error(`Failed to fetch ticket audit trails:`, error)
    } finally {
      auditTrailState.setIsLoading(false)
    }
  }

  const fetchContractorQuotation = async () => {
    contractorQuotationState.setIsLoading(true)
    try {
      const response = await searchContractorQuotation(
        contractorQuotationState.ticketId,
      )
      const contractorData = response.data.data

      return contractorData
    } catch (error) {
      console.error(`Failed to contractor quotation:`, error)
    } finally {
      contractorQuotationState.setIsLoading(false)
    }
  }

  const fetchPurchaseRequest = async () => {
    purchaseRequestState.setIsLoading(true)
    try {
      const response = await searchPurchaseRequest(
        purchaseRequestState.ticketId,
      )
      return response.data.data
    } catch (error) {
      console.error(`Failed to fetch purchase requests:`, error)
    } finally {
      purchaseRequestState.setIsLoading(false)
    }
  }

  const fetchPurchaseOrder = async () => {
    purchaseOrderState.setIsLoading(true)
    try {
      const response = await searchPurchaseOrder(purchaseOrderState.ticketId)
      return response.data.data
    } catch (error) {
      console.error(`Failed to fetch purchase requests:`, error)
    } finally {
      purchaseOrderState.setIsLoading(false)
    }
  }

  const { data: purchaseRequest, mutate: mutatePurchaseRequest } = useSWR(
    `/v1/ticket/purchase-request/quotation/${purchaseRequestState.ticketId}?page=0&size=9999&sort=`,
    () => fetchPurchaseRequest(),
    { revalidateOnFocus: false },
  )

  const { data: purchaseOrder, mutate: mutatePurchaseOrder } = useSWR(
    `/v1/ticket/purchase-order/quotation/${purchaseOrderState.ticketId}?page=0&size=9999&sort=`,
    () => fetchPurchaseOrder(),
    { revalidateOnFocus: false },
  )

  const { data: contractorQuotation, mutate: mutateContractorQuotation } =
    useSWR(
      `/v1/ticket/contractor/quotation/${contractorQuotationState.ticketId}?page=0&size=9999&sort=`,
      () => fetchContractorQuotation(),
      { revalidateOnFocus: false },
    )

  const { data: auditTrail, mutate: mutateAuditTrail } = useSWR(
    `/v1/audit-trail/ticket-history?page=${auditTrailState.page}&size=99999999&ticketId=${auditTrailState.ticketId}&keyword=${auditTrailState.keyword}&data=${auditTrailState.dataFilters}`,
    () => fetchTicketAuditTrails(),
    { revalidateOnFocus: false },
  )

  const [maintenanceTicketsState, dispatchMaintenanceTickets] = useReducer(
    maintenanceTicketsReducer,
    maintenanceTicketsInitialState,
  )

  const stateRef = useRef(maintenanceTicketsState)
  useEffect(() => {
    stateRef.current = maintenanceTicketsState
  }, [maintenanceTicketsState])

  useEffect(() => {
    mutateAuditTrail()
  }, [
    auditTrailState.dateFilters,
    auditTrailState.page,
    auditTrailState.rowsPerPage,
    auditTrailState.ticketId,
  ])

  useEffect(() => {
    if (
      role === 'GA Purchasing Staff' &&
      contractorQuotationState.ticketId !== 0
    )
      mutateContractorQuotation()
    if (role === 'DND Purchasing Admin' && purchaseRequestState.ticketId !== 0)
      mutatePurchaseRequest()

    if (role === 'GA Purchasing Staff' && purchaseOrderState.ticketId !== 0)
      mutatePurchaseOrder()
  }, [
    contractorQuotationState.ticketId,
    purchaseRequestState.ticketId,
    purchaseOrderState.ticketId,
  ])

  const value = useMemo(
    () => ({
      state: maintenanceTicketsState,
      actions: maintenanceTicketsActions,
      dispatch: dispatchMaintenanceTickets,
      processing,
      processed,
      searchTickets,
      filterTickets,
      resetFilters,
      processingState,
      processedState,
      selectedTab,
      setSelectedTab: handleTabChange,
      role,
      bmProcessed,
      bmState,
      auditTrail,
      auditTrailState,
      viewTicketAuditTrail,
      filterAuditTrail,
      resetAuditTrailFilter,
      mutateBmProcessed,
      mutateForProcessing,
      filterDataAuditTrail,
      contractorQuotation,
      contractorQuotationState,
      mutateContractorQuotation,
      viewQuotationForm,
      viewPurchaseRequest,
      mutatePurchaseRequest,
      purchaseRequestState,
      purchaseRequest,
      purchaseOrder,
      purchaseOrderState,
      viewPurchaseOrder,
      mutatePurchaseOrder,
      filterTicketsMethodRepair,
      filterTicketsStatus,
      filterTicketsTypeConcern,
      mutateProcessed,
      filterSortedTickets,
    }),
    [
      maintenanceTicketsState,
      processing,
      processed,
      selectedTab,
      processingState,
      processedState,
      role,
      bmProcessed,
      bmState,
      auditTrail,
      auditTrailState,
      viewTicketAuditTrail,
      filterAuditTrail,
      resetAuditTrailFilter,
      mutateBmProcessed,
      mutateForProcessing,
      filterDataAuditTrail,
      contractorQuotation,
      contractorQuotationState,
      mutateContractorQuotation,
      viewQuotationForm,
      viewPurchaseRequest,
      mutatePurchaseRequest,
      purchaseRequestState,
      purchaseRequest,
      purchaseOrder,
      purchaseOrderState,
      viewPurchaseOrder,
      mutatePurchaseOrder,
    ],
  )

  return (
    <MaintenanceTicketsContext.Provider value={value}>
      {children}
    </MaintenanceTicketsContext.Provider>
  )
}
